import React from 'react';
import {
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  Box,
  Typography,
} from '@mui/material';

/**
 * FormRadio - Reusable radio group component with Notched Outline label
 */
const FormRadio = React.forwardRef((props, ref) => {
  const {
    name,
    label,
    options = [],
    value = '',
    onChange,
    required = false,
    disabled = false,
    error = false,
    helperText = '',
    row = true,
    className,
    sx = {},
    ...rest
  } = props;

  // We only render the legend/label if a text label is actually provided
  const hasLabel = label && label.trim() !== "";

  return (
    <Box ref={ref} key={name} sx={{ml: 0, width: '100%', ...sx }}>
      {row ? (
        // Horizontal layout: Notched outline mimicking Material-UI TextField
        <Box 
          component="fieldset" 
          sx={{ 
            px: 1.5, 
            py: 0.5,
            pb: 1,
            mt: "2.5px",
            border: '1px solid',
            borderColor: error ? 'error.main' : (t) => t.palette.mode === 'dark' ? 'rgba(219, 231, 246, 0.12)' : 'rgba(0, 0, 0, 0.23)',
            borderRadius: '4px',
          }}
        >
          {/* The legend acts as the floating label and automatically breaks the border */}
          {hasLabel && (
            <Typography 
              component="legend" 
              sx={{ 
                fontSize: '12px', 
                color: error ? 'error.main' : (t) => t.palette.mode === 'dark' ? '#aeb9c6' : '#4c5a69', 
                px: 0.5, // Padding creates the gap in the border line
                width: 'auto',
                mb: 0,
                lineHeight: 1
              }}
            >
              {label}
              {required ? ' *' : ''}
            </Typography>
          )}
          
          <FormControl component="fieldset" disabled={disabled} error={error} fullWidth>
            <RadioGroup
              name={name}
              value={value}
              onChange={onChange}
              row={row}
              sx={{ 
                display: 'flex',
                flexDirection: 'row',
                gap: 2,
                mt: hasLabel ? -0.5 : 0, 
              }}
              {...rest}
            >
              {options.map((option) => (
                <FormControlLabel
                  key={option.value}
                  value={option.value}
                  control={<Radio size="small" />}
                  label={<Typography sx={{ fontSize: '14px', color: 'text.primary' }}>{option.label}</Typography>}
                  disabled={option.isEnable === false}
                  sx={{ m: 0, mr: 1 }}
                />
              ))}
            </RadioGroup>
          </FormControl>
        </Box>
      ) : (
        <Box>
          {hasLabel && (
            <Typography 
              variant="subtitle1" 
              //  FIXED: Added color: 'text.primary' for vertical layout title
              sx={{ fontSize: '14px', mb: 1, fontWeight: 700, color: 'text.primary' }}
            >
              {label}
              {required ? ' *' : ''}
            </Typography>
          )}
          {options.map((option) => (
            <Box 
              key={option.value}
              sx={{ 
                px: 2, py: 1.5,
                border: '1px solid',
                //  FIXED: Dynamically swap border color based on theme for vertical layout
                borderColor: error ? 'error.main' : (t) => t.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.23)' : 'rgba(0, 0, 0, 0.23)',
                borderRadius: '4px',
                mb: 1,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <FormControl component="fieldset" disabled={disabled} fullWidth>
                <RadioGroup
                  name={name}
                  value={value}
                  onChange={onChange}
                  sx={{ m: 0, display: 'flex' }}
                >
                  <FormControlLabel
                    value={option.value}
                    control={<Radio size="small" />}
                    //  FIXED: Added color: 'text.primary'
                    label={<Typography sx={{ fontSize: '14px', color: 'text.primary' }}>{option.label}</Typography>}
                    disabled={option.isEnable === false}
                    sx={{ m: 0 }}
                  />
                </RadioGroup>
              </FormControl>
            </Box>
          ))}
        </Box>
      )}
      
      {/* Helper text perfectly aligned with standard MUI helper texts */}
      {error && (
        <Typography color="error" sx={{ fontSize: '0.75rem', mt: 0.5, mx: 1.5 }}>
          {helperText}
        </Typography>
      )}
    </Box>
  );
});

FormRadio.displayName = 'FormRadio';

export default FormRadio;